import {
  DOCUMENT_TEMPLATES,
  type DocumentTemplate,
  type TemplateSectionId,
} from "../constants/documentTemplates";
import type { NewDocumentTranslations } from "@/lib/i18n/types/documents-sections/newDocument";

/** One rendered template card. `key` is section-qualified so it stays unique
 *  if a template is ever surfaced in more than one section. */
export interface TemplateCardItem {
  key: string;
  template: DocumentTemplate;
  name: string;
  /** Both languages, for the card's tooltip. Equal to `name` when there is
   *  nothing to disclose, which is how the card decides to skip the tooltip. */
  fullName: string;
  description: string;
  /** The card's ⓘ tooltip — what the template is for. */
  details: string;
}

export interface TemplateSection {
  id: TemplateSectionId;
  title: string;
  items: TemplateCardItem[];
}

/** Lower-cased and stripped of diacritics, so "tooded" matches "tööde". */
export function normaliseSearchText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

function toCardItem(
  template: DocumentTemplate,
  sectionId: TemplateSectionId,
  copy: NewDocumentTranslations
): TemplateCardItem {
  const templateCopy = copy.templates[template.id];
  return {
    key: `${sectionId}:${template.id}`,
    template,
    name: templateCopy.name,
    fullName: templateCopy.fullName,
    description: templateCopy.description,
    details: templateCopy.details,
  };
}

/**
 * Builds every section in visual order.
 *
 * There is deliberately no "Recommended for {building}" section: it was a
 * hardcoded pair of templates shown to every building in every org, chipped
 * "Suggested by Franklin" — a claim of intelligence behind a constant. It
 * comes back when there is a real signal to base it on (overdue checks, what
 * the project has been filing), not before.
 */
export function buildTemplateSections(copy: NewDocumentTranslations): TemplateSection[] {
  const sections: TemplateSection[] = [];

  for (const sectionId of ["franklin"] as const) {
    const items = DOCUMENT_TEMPLATES.filter((tpl) => tpl.section === sectionId).map((tpl) =>
      toCardItem(tpl, sectionId, copy)
    );
    if (items.length > 0) {
      sections.push({ id: sectionId, title: copy.sections[sectionId], items });
    }
  }

  return sections;
}

/** Filters on the bilingual name, the description and the ⓘ explanation;
 *  sections left with no matches disappear. `fullName` carries both languages, so an English user searching
 *  "kaetud" finds the act and an Estonian one searching "diary" finds the
 *  päevik — the documents are known by both names on site. */
export function filterTemplateSections(
  sections: TemplateSection[],
  query: string
): TemplateSection[] {
  const needle = normaliseSearchText(query);
  if (!needle) return sections;

  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        normaliseSearchText(`${item.fullName} ${item.description} ${item.details}`).includes(needle)
      ),
    }))
    .filter((section) => section.items.length > 0);
}

/** Keys of the cards ↑/↓ can land on, flattened across sections in visual order. */
export function selectableTemplateKeys(sections: TemplateSection[]): string[] {
  return sections.flatMap((section) =>
    section.items.filter((item) => !item.template.disabled).map((item) => item.key)
  );
}

/** Moves the highlight by `delta`, wrapping at both ends. */
export function moveHighlight(
  keys: string[],
  current: string | null,
  delta: number
): string | null {
  if (keys.length === 0) return null;
  const index = current ? keys.indexOf(current) : -1;
  if (index === -1) return delta > 0 ? keys[0] : keys[keys.length - 1];
  return keys[(index + delta + keys.length) % keys.length];
}

export function findTemplateItem(
  sections: TemplateSection[],
  key: string | null
): TemplateCardItem | undefined {
  if (!key) return undefined;
  for (const section of sections) {
    const match = section.items.find((item) => item.key === key);
    if (match) return match;
  }
  return undefined;
}
